import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import FormRenderer from '../components/FormRenderer';
import FormSubmitSuccess from '../components/FormSubmitSuccess';

function FormPage() {
  const { formToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [clientInfo, setClientInfo] = useState({ name: '', email: '' });

  useEffect(() => {
    loadForm();
  }, [formToken]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const data = await api.getForm(formToken);
      setFormData(data);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This form has already been submitted.');
      } else if (err.response?.status === 404) {
        setError('This form link is invalid or has expired.');
      } else {
        setError('Failed to load form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data) => {
    if (formData.isPerpetual && (!clientInfo.name.trim() || !clientInfo.email.trim())) {
      alert('Please enter your name and email address.');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = formData.isPerpetual
        ? { ...data, _clientName: clientInfo.name.trim(), _clientEmail: clientInfo.email.trim() }
        : data;
      await api.submitForm(formToken, submitData);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This form has already been submitted.');
      } else if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to submit form. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Form</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow-sm rounded-lg p-6 md:p-8">
          {submitted ? (
            <FormSubmitSuccess formTitle={formData.title} />
          ) : (
            <>
              <div className="mb-6">
                {formData.clientName && (
                  <p className="text-sm text-primary-600 font-medium mb-1">{formData.clientName}</p>
                )}
                <h1 className="text-2xl font-bold text-gray-900">{formData.title}</h1>
                {formData.description && (
                  <p className="text-gray-600 mt-2">{formData.description}</p>
                )}
              </div>

              <hr className="mb-6" />

              {formData.isPerpetual && (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={clientInfo.name}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={clientInfo.email}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="you@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <hr />
                </div>
              )}

              <FormRenderer
                fields={formData.fields}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by Impetra
        </p>
      </div>
    </div>
  );
}

export default FormPage;
